// AI assisted development
/**
 * FeedbackFormDialog Component
 * Feature: 012-user-feedback
 * 
 * Dialog form for creating new feedback items.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useCreateFeedback, useUpdateFeedback } from '../../hooks/useFeedback';
import type { CreateFeedbackRequest, UpdateFeedbackRequest, FeedbackItem } from '../../types/feedback';

interface FeedbackFormDialogProps {
  open: boolean;
  onClose: () => void;
  editItem?: FeedbackItem | null;
  pageTitle?: string;
  pageUrl?: string;
  source?: string;
}

export default function FeedbackFormDialog({
  open,
  onClose,
  editItem,
  pageTitle,
  pageUrl,
  source = 'General',
}: FeedbackFormDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notifyOnStatusChange, setNotifyOnStatusChange] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateFeedback();
  const updateMutation = useUpdateFeedback();

  // Pre-fill form when editing
  useEffect(() => {
    if (editItem) {
      setTitle(editItem.title);
      setDescription(editItem.description);
      setNotifyOnStatusChange(editItem.notifyOnStatusChange ?? true);
    } else {
      setTitle('');
      setDescription('');
      setNotifyOnStatusChange(true);
    }
    setError(null);
  }, [editItem, open]);

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (title.length > 200) {
      setError('Title must be 200 characters or less');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    if (description.length > 2000) {
      setError('Description must be 2000 characters or less');
      return;
    }

    setError(null);

    try {
      if (editItem) {
        // Update existing feedback
        const request: UpdateFeedbackRequest = {
          title: title.trim(),
          description: description.trim(),
          notifyOnStatusChange,
        };
        await updateMutation.mutateAsync({ id: editItem.id, request });
      } else {
        // Create new feedback
        const request: CreateFeedbackRequest = {
          title: title.trim(),
          description: description.trim(),
          source,
          pageTitle: pageTitle || null,
          pageUrl: pageUrl || null,
          notifyOnStatusChange,
        };
        await createMutation.mutateAsync(request);
      }
      // Reset form and close
      setTitle('');
      setDescription('');
      setNotifyOnStatusChange(true);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setNotifyOnStatusChange(true);
    setError(null);
    onClose();
  };

  const isEditMode = !!editItem;
  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Feedback' : 'Share Feedback'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!isEditMode && pageTitle && (
            <Alert severity="info">
              This feedback will be linked to {pageTitle}.
            </Alert>
          )}
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 200 }}
            helperText={`${title.length}/200 characters`}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            required
            multiline
            rows={4}
            inputProps={{ maxLength: 2000 }}
            helperText={`${description.length}/2000 characters`}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={notifyOnStatusChange}
                onChange={(event) => setNotifyOnStatusChange(event.target.checked)}
              />
            }
            label="Notify me when this feedback status changes"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !title.trim() || !description.trim()}
        >
          {isLoading ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update' : 'Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

